/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from "react"
import { Form, Input, Button, DatePicker, Select, Collapse, Row, Col, InputNumber, Upload, AutoComplete, Spin } from "antd"
import { FileTextOutlined, ToolOutlined, DashboardOutlined, InfoCircleOutlined, UploadOutlined, CameraOutlined } from "@ant-design/icons"
import moment from "moment"
import { useActivitiesStore, useDepartmentsStore, useEquipmentsStore, useLocationsStore, useOperatorsStore, useUserStore } from "@/stores"
import debounce from "lodash/debounce"

const { Panel } = Collapse
const { Option } = Select
const { TextArea } = Input
const { RangePicker } = DatePicker

const normFile = (e: { fileList: string[] }) => {
  if (!!e && Array.isArray(e)) {
    return e
  }
  return e?.fileList
}

interface Props {
  isEditMode: boolean
  initialValues?: any
  onFinish: (values: any) => void
  loading: boolean
}

const HeTimesheetForm: React.FC<Props> = ({ isEditMode, initialValues, onFinish, loading }) => {
  const { userData } = useUserStore()
  const isAdmin = userData?.roles?.includes("ROLE_ADMIN")
  const { departments, isLoading: loadingDepartments, fetchDepartments } = useDepartmentsStore()
  const { equipments, fetchEquipments } = useEquipmentsStore()
  const { operators, isLoading: loadingOperators, fetchOperators } = useOperatorsStore()
  const { locations, isLoading: loadingLocations, fetchLocations } = useLocationsStore()
  const { activities, isLoading: loadingActivities, fetchActivities } = useActivitiesStore()
  const [form] = Form.useForm()
  const [isDrilling, setIsDrilling] = useState<boolean>(false)
  const [isManualHullInput, setIsManualHullInput] = useState<boolean>(false)
  const [isManualOperatorInput, setIsManualOperatorInput] = useState<boolean>(false)
  const [isSearchingEquipments, setIsSearchingEquipments] = useState<boolean>(false)
  const [isSearchingOperators, setIsSearchingOperators] = useState<boolean>(false)
  const [isSearchingLocations, setIsSearchingLocations] = useState<boolean>(false)
  const [isSearchingActivities, setIsSearchingActivities] = useState<boolean>(false)
  const disabledDate = (current: any) => current && current > moment().endOf("day")
  const disabledRangeTime = (_: any, __: "start" | "end") => ({})

  const handleOperatorChange = (value: string) => {
    const selectedOperator = operators.find(op => op.name === value)
    if (selectedOperator) {
      form.setFieldsValue({
        operator_id: selectedOperator.nik,
      })
      setIsManualOperatorInput(false)
      return
    }

    setIsManualOperatorInput(!!value)
    form.setFieldsValue({
      operator_id: undefined,
    })
  }

  const handleHullCodeChange = (value: string) => {
    const selectedEquipment = equipments.find(eq => eq.unit.code === value)
    if (selectedEquipment) {
      form.setFieldsValue({
        hull_name: selectedEquipment.unit?.name,
        vendor_name: selectedEquipment.company?.name,
      })
      setIsDrilling(selectedEquipment.unit?.category === "Drilling")
      setIsManualHullInput(false)
      return
    }

    setIsDrilling(false)
    setIsManualHullInput(!!value)
    form.setFieldsValue({
      hull_name: undefined,
      vendor_name: undefined,
    })
  }

  const handleValuesChange = (
    changedValues: { hm_start: number; hm_stop: number; depth_meter_from?: number; depth_meter_to?: number; depth_meter_total?: number },
    allValues: { hm_start: number; hm_stop: number; depth_meter_from?: number; depth_meter_to?: number; depth_meter_total?: number }
  ) => {
    if ("hm_start" in changedValues || "hm_stop" in changedValues) {
      const start = allValues.hm_start
      const stop = allValues.hm_stop
      if (start !== undefined && stop !== undefined) {
        const result = stop - start
        form.setFieldsValue({
          hm_total: parseFloat(result.toFixed(2)),
        })
      }
    }

    if ("depth_meter_from" in changedValues || "depth_meter_to" in changedValues) {
      const from = allValues.depth_meter_from
      const to = allValues.depth_meter_to
      if (from !== undefined && to !== undefined) {
        const result = to - from
        form.setFieldsValue({
          depth_meter_total: parseFloat(result.toFixed(2)),
        })
      } else {
        form.setFieldsValue({
          depth_meter_total: undefined,
        })
      }
    }
  }

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue({
        ...initialValues,
        branch_name: userData?.branch_nickname,
        date_range:
          initialValues.date_from && initialValues.date_to
            ? [moment(initialValues.date_from), moment(initialValues.date_to)]
            : initialValues.start_date && initialValues.end_date
              ? [moment(initialValues.start_date), moment(initialValues.end_date)]
              : initialValues.start_time && initialValues.end_time
                ? [moment(initialValues.start_time), moment(initialValues.end_time)]
              : null,
      })
    } else {
      form.setFieldsValue({
        date_range: [moment(), moment()],
        branch_name: userData?.branch_nickname,
        shift_type: "PAGI",
      })
    }
  }, [initialValues, form, userData])

  const handleSearchEquipments = React.useMemo(
    () =>
      (debounce as any)(async (value: string) => {
        setIsSearchingEquipments(true)
        try {
          await fetchEquipments({ page: 1, limit: 10, search: value })
        } finally {
          setIsSearchingEquipments(false)
        }
      }, 500) as unknown as (value: string) => void,
    [fetchEquipments]
  )

  const handleSearchOperators = React.useMemo(
    () =>
      (debounce as any)(async (value: string) => {
        setIsSearchingOperators(true)
        try {
          await fetchOperators({ page: 1, limit: 10, search: value })
        } finally {
          setIsSearchingOperators(false)
        }
      }, 500) as unknown as (value: string) => void,
    [fetchOperators]
  )

  const handleSearchLocations = React.useMemo(
    () =>
      (debounce as any)(async (value: string) => {
        setIsSearchingLocations(true)
        try {
          await fetchLocations({ page: 1, limit: 10, search: value })
        } finally {
          setIsSearchingLocations(false)
        }
      }, 500) as unknown as (value: string) => void,
    [fetchLocations]
  )

  const handleSearchActivities = React.useMemo(
    () =>
      (debounce as any)(async (value: string) => {
        setIsSearchingActivities(true)
        try {
          await fetchActivities({ page: 1, limit: 10, search: value })
        } finally {
          setIsSearchingActivities(false)
        }
      }, 500) as unknown as (value: string) => void,
    [fetchActivities]
  )

  const handleSearchDepartments = React.useMemo(
    () =>
      (debounce as any)(async (value: string) => {
        await fetchDepartments({ page: 1, limit: 100, search: value })
      }, 500) as unknown as (value: string) => void,
    [fetchDepartments]
  )

  useEffect(() => {
    if (initialValues?.hull_name && equipments?.length) {
      const selectedEq = equipments.find(eq => eq.asset_number === initialValues.hull_code)
      if (selectedEq) {
        setIsDrilling(selectedEq.unit?.category === "Drilling")
        setIsManualHullInput(false)
      } else if (initialValues.hull_code) {
        setIsDrilling(false)
        setIsManualHullInput(true)
      }
    }
  }, [initialValues, equipments])

  useEffect(() => {
    if (!initialValues?.operator_name) return

    const selectedOperator = operators.find(op => op.name === initialValues.operator_name)
    if (selectedOperator) {
      setIsManualOperatorInput(false)
      return
    }

    setIsManualOperatorInput(true)
  }, [initialValues, operators])

  useEffect(() => {
    void fetchDepartments({ page: 1, limit: 100 })
  }, [fetchDepartments])

  useEffect(() => {
    if (!equipments.length) {
      void fetchEquipments({ page: 1, limit: 10 })
    }
    if (!operators.length) {
      void fetchOperators({ page: 1, limit: 10 })
    }
    if (!locations.length) {
      void fetchLocations({ page: 1, limit: 10 })
    }
    if (!activities.length) {
      void fetchActivities({ page: 1, limit: 10 })
    }
  }, [
    fetchEquipments,
    fetchOperators,
    fetchLocations,
    fetchActivities,
    equipments.length,
    operators.length,
    locations.length,
    activities.length,
  ])

  return (
    <Form form={form} layout="vertical" onFinish={onFinish} onValuesChange={handleValuesChange} style={{ maxWidth: 1000, margin: "0 auto" }}>
      <Collapse defaultActiveKey={["1"]} ghost style={{ marginBottom: 24, background: "#fff", borderRadius: 8 }}>
        <Panel
          header={
            <>
              <ToolOutlined /> Unit & Operation
            </>
          }
          key="1"
        >
          <Row gutter={24}>
            <Col span={12} xs={24} md={12}>
              <Form.Item label="Equipment Code" name="hull_code" rules={[{ required: true, message: "Please select equipment code" }]}>
                <Select
                  placeholder="Select equipment code"
                  disabled={loading}
                  showSearch
                  filterOption={false}
                  onChange={handleHullCodeChange}
                  onSearch={handleSearchEquipments}
                  loading={isSearchingEquipments}
                  notFoundContent={isSearchingEquipments ? <Spin size="small" /> : null}
                >
                  {(equipments || []).map(eq => (
                    <Option key={eq.unit.code} value={eq.unit.code}>
                      {eq.unit.code}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12} xs={24} md={12}>
              <Form.Item label="Equipment Name" name="hull_name" rules={[{ required: true, message: "Please select equipment name" }]}>
                <Input disabled={!isManualHullInput || loading} placeholder={isManualHullInput ? "Input equipment name" : "Auto filled Equipment Name"} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col span={12} xs={24} md={12}>
              <Form.Item label="Company Name" name="vendor_name" rules={[{ required: true, message: "Please enter company name" }]}>
                <Input placeholder={isManualHullInput ? "Input company name" : "Auto-filled Company Name"} disabled={!isManualHullInput || loading} />
              </Form.Item>
            </Col>
            <Col span={12} xs={24} md={12}>
              <Form.Item label="Date Range" name="date_range" rules={[{ required: true, message: "Please select date range" }]}>
                <RangePicker
                  style={{ width: "100%" }}
                  disabled={loading}
                  disabledDate={disabledDate}
                  disabledTime={disabledRangeTime}
                  showTime={{
                    hideDisabledOptions: true,
                    defaultValue: [moment("00:00", "HH:mm"), moment("11:59", "HH:mm")],
                  }}
                  format="YYYY-MM-DD HH:mm"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col span={12} xs={24} md={12}>
              <Form.Item label="Operator Name" name="operator_name" rules={[{ required: true, message: "Please select operator name" }]}>
                <AutoComplete
                  placeholder="Select or type operator name"
                  disabled={loading}
                  onChange={handleOperatorChange}
                  onSelect={handleOperatorChange}
                  onSearch={handleSearchOperators}
                  options={(operators || []).map(op => ({
                    value: op.name,
                    label: op.name,
                  }))}
                >
                  <Input suffix={(isSearchingOperators || loadingOperators) && <Spin size="small" />} />
                </AutoComplete>
              </Form.Item>
            </Col>
            <Col span={12} xs={24} md={12}>
              <Form.Item label="Operator ID" name="operator_id">
                <Input placeholder={isManualOperatorInput ? "Input operator ID" : "Auto-filled Operator ID"} disabled={!isManualOperatorInput || loading} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col span={12} xs={24} md={12}>
              <Form.Item label="Department" name="department_name" rules={[{ required: true, message: "Please select department" }]}>
                <Select
                  placeholder="Select department"
                  disabled={loading}
                  loading={loadingDepartments}
                  showSearch
                  filterOption={false}
                  onSearch={handleSearchDepartments}
                >
                  {(departments || []).map(department => (
                    <Option key={department.id} value={department.name}>
                      {department.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Panel>
      </Collapse>

      <Collapse defaultActiveKey={["1", "2"]} ghost style={{ marginBottom: 24, background: "#fff", borderRadius: 8 }}>
        <Panel
          header={
            <>
              <FileTextOutlined /> General Information
            </>
          }
          key="1"
        >
          <Row gutter={24}>
            <Col span={12} xs={24} md={12}>
              <Form.Item label="Shift Type" name="shift_type" rules={[{ required: true, message: "Please select shift type" }]}>
                <Select placeholder="Select shift type" disabled={loading}>
                  <Option value="PAGI">Morning Shift</Option>
                  <Option value="MALAM">Night Shift</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12} xs={24} md={12}>
              <Form.Item label="Branch Name" name="branch_name" rules={[{ required: true, message: "Please select branch name" }]}>
                <Input placeholder="Enter branch name" disabled readOnly />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col span={12} xs={24} md={12}>
              <Form.Item label="Job Location" name="job_location" rules={[{ required: true, message: "Please select location" }]}>
                <AutoComplete
                  placeholder="Select or type job location"
                  disabled={loading}
                  showSearch
                  onSearch={handleSearchLocations}
                  options={(locations || []).map(location => ({
                    value: location.location,
                    label: location.location,
                  }))}
                >
                  <Input suffix={(isSearchingLocations || loadingLocations) && <Spin size="small" />} />
                </AutoComplete>
              </Form.Item>
            </Col>
            <Col span={12} xs={24} md={12}>
              <Form.Item label="Activity Name" name="activity_name" rules={[{ required: true, message: "Please select activity" }]}>
                <AutoComplete
                  placeholder="Select or type activity"
                  disabled={loading}
                  showSearch
                  onSearch={handleSearchActivities}
                  options={(activities || []).map(activity => ({
                    value: activity.segment_name,
                    label: activity.segment_name,
                  }))}
                >
                  <Input suffix={(isSearchingActivities || loadingActivities) && <Spin size="small" />} />
                </AutoComplete>
              </Form.Item>
            </Col>
          </Row>
        </Panel>
      </Collapse>

      <Collapse defaultActiveKey={["1", "2", "3"]} ghost style={{ marginBottom: 24, background: "#fff", borderRadius: 8 }}>
        <Panel
          header={
            <>
              <DashboardOutlined /> Hours & Metrics
            </>
          }
          key="2"
        >
          <Row gutter={24}>
            <Col span={8} xs={24} md={8}>
              <Form.Item
                label="HM Start"
                name="hm_start"
                rules={[
                  { required: true, message: "Required" },
                  { type: "number", message: "Must be a number" },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  placeholder="0"
                  min={0}
                  disabled={loading}
                  parser={value => value?.replace(/[^\d.]/g, "") as unknown as number}
                  onKeyPress={event => {
                    if (!/[0-9.]/.test(event.key)) {
                      event.preventDefault()
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={8} xs={24} md={8}>
              <Form.Item
                label="HM Stop"
                name="hm_stop"
                dependencies={["hm_start"]}
                rules={[
                  { required: true, message: "Required" },
                  { type: "number", message: "Must be a number" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("hm_start") === undefined) {
                        return Promise.resolve()
                      }
                      if (value < getFieldValue("hm_start")) {
                        return Promise.reject(new Error("HM Stop must be greater than HM Start"))
                      }
                      return Promise.resolve()
                    },
                  }),
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  placeholder="0"
                  min={0}
                  disabled={loading}
                  parser={value => value?.replace(/[^\d.]/g, "") as unknown as number}
                  onKeyPress={event => {
                    if (!/[0-9.]/.test(event.key)) {
                      event.preventDefault()
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={8} xs={24} md={8}>
              <Form.Item
                label="HM Total"
                name="hm_total"
                rules={[
                  { required: true, message: "Required" },
                  { type: "number", message: "Must be a number" },
                ]}
              >
                <InputNumber style={{ width: "100%" }} placeholder="0" disabled={true} parser={value => value?.replace(/[^\d.]/g, "") as unknown as number} />
              </Form.Item>
            </Col>
          </Row>
          {isDrilling && (
            <Row gutter={24}>
              <Col span={12} xs={24} md={8}>
                <Form.Item
                  label="Depth Meter From"
                  name="depth_meter_from"
                  rules={[
                    { type: "number", message: "Must be a number" },
                  ]}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    placeholder="0"
                    min={0}
                    disabled={loading}
                    parser={value => value?.replace(/[^\d.]/g, "") as unknown as number}
                    onKeyPress={event => {
                      if (!/[0-9.]/.test(event.key)) {
                        event.preventDefault()
                      }
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={12} xs={24} md={8}>
                <Form.Item
                  label="Depth Meter To"
                  name="depth_meter_to"
                  rules={[
                    { type: "number", message: "Must be a number" },
                  ]}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    placeholder="0"
                    min={0}
                    disabled={loading}
                    parser={value => value?.replace(/[^\d.]/g, "") as unknown as number}
                    onKeyPress={event => {
                      if (!/[0-9.]/.test(event.key)) {
                        event.preventDefault()
                      }
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={24} xs={24} md={8}>
                <Form.Item
                  label="Depth Meter Total"
                  name="depth_meter_total"
                  rules={[
                    { type: "number", message: "Must be a number" },
                  ]}
                >
                  <InputNumber style={{ width: "100%" }} placeholder="Auto calculated" disabled={true} parser={value => value?.replace(/[^\d.]/g, "") as unknown as number} />
                </Form.Item>
              </Col>
            </Row>
          )}
        </Panel>
      </Collapse>

      <Collapse defaultActiveKey={["3"]} ghost style={{ marginBottom: 24, background: "#fff", borderRadius: 8 }}>
        <Panel
          header={
            <>
              <CameraOutlined /> Evidence & Photos
            </>
          }
          key="3"
        >
          <Row gutter={24}>
            {!isAdmin && (
              <Col span={12} xs={24} md={12}>
                <Form.Item label="HM Start Photo" name="hm_start_photo" valuePropName="fileList" getValueFromEvent={normFile} rules={[{ required: true, message: "Please upload HM Start Photo" }]}>
                  <Upload accept=".jpg, .jpeg, .png, image/png, image/jpeg" name="logo" listType="picture" maxCount={1} beforeUpload={() => false} disabled={loading}>
                    <Button icon={<UploadOutlined />}>Click to upload</Button>
                  </Upload>
                </Form.Item>
              </Col>
            )}
            {!isAdmin && (
              <Col span={12} xs={24} md={12}>
                <Form.Item label="HM Stop Photo" name="hm_stop_photo" valuePropName="fileList" getValueFromEvent={normFile} rules={[{ required: true, message: "Please upload HM Stop Photo" }]}>
                  <Upload accept=".jpg, .jpeg, .png, image/png, image/jpeg" name="logo" listType="picture" maxCount={1} beforeUpload={() => false} disabled={loading}>
                    <Button icon={<UploadOutlined />}>Click to upload</Button>
                  </Upload>
                </Form.Item>
              </Col>
            )}
            <Col span={12} xs={24} md={12}>
              <Form.Item
                label="Timesheet Attachment"
                name="unit_photo"
                valuePropName="fileList"
                getValueFromEvent={normFile}
                rules={[{ required: true, message: "Please upload attachment" }]}
                extra="Please upload a clear photo of the timesheet."
              >
                <Upload accept=".jpg, .jpeg, .png, image/png, image/jpeg" name="logo" listType="picture" maxCount={1} beforeUpload={() => false} disabled={loading}>
                  <Button icon={<UploadOutlined />}>Click to upload</Button>
                </Upload>
              </Form.Item>
            </Col>
            {!isAdmin && (
              <Col span={12} xs={24} md={12}>
                <Form.Item label="Operator Selfie" name="operator" valuePropName="fileList" getValueFromEvent={normFile} rules={[{ required: true, message: "Please upload Operator Selfie" }]}>
                  <Upload accept=".jpg, .jpeg, .png, image/png, image/jpeg" name="logo" listType="picture" maxCount={1} beforeUpload={() => false} disabled={loading}>
                    <Button icon={<UploadOutlined />}>Click to upload</Button>
                  </Upload>
                </Form.Item>
              </Col>
            )}
          </Row>
        </Panel>
      </Collapse>

      <Collapse defaultActiveKey={["4"]} ghost style={{ marginBottom: 24, background: "#fff", borderRadius: 8 }}>
        <Panel
          header={
            <>
              <InfoCircleOutlined /> Additional Details
            </>
          }
          key="4"
        >
          <Row gutter={24}>
            <Col span={24}>
              <Form.Item label="Note" name="note">
                <TextArea rows={2} placeholder="Enter note" disabled={loading} />
              </Form.Item>
            </Col>
          </Row>
        </Panel>
      </Collapse>

      <Form.Item>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
          <Button onClick={() => window.history.back()}>Cancel</Button>
          <Button type="primary" htmlType="submit" loading={loading} style={{ minWidth: 120 }}>
            {isEditMode ? "Update Timesheet" : "Create Timesheet"}
          </Button>
        </div>
      </Form.Item>
    </Form>
  )
}

export default HeTimesheetForm
